import type { CategoryDTO } from "../api/client";

export type CategoryTreeNode = CategoryDTO & {
  children: CategoryTreeNode[];
};

/** สร้างต้นไม้จากรายการแบน (parentId) — เรียงชื่อในแต่ละระดับ */
export function buildCategoryTree(
  items: CategoryDTO[],
): CategoryTreeNode[] {
  const byId = new Map<string, CategoryTreeNode>();
  for (const item of items) {
    byId.set(item.id, { ...item, children: [] });
  }

  const roots: CategoryTreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortRecursive = (nodes: CategoryTreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name, "lo"));
    for (const n of nodes) sortRecursive(n.children);
  };
  sortRecursive(roots);
  return roots;
}

/** ตัวเลือก parent แบบ indent ตามลำดับชั้น */
export function flattenCategoryTreeOptions(
  tree: CategoryTreeNode[],
  excludeId?: string,
): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = [];

  const walk = (nodes: CategoryTreeNode[], depth: number) => {
    for (const n of nodes) {
      if (n.id === excludeId) continue;
      const pad = depth > 0 ? `${"— ".repeat(depth)}` : "";
      out.push({ value: n.id, label: `${pad}${n.name}` });
      if (n.children.length) walk(n.children, depth + 1);
    }
  };

  walk(tree, 0);
  return out;
}

/**
 * กรองต้นไม้ตามคำค้น — คงโหนดที่ตรงชื่อ หรือมีลูกที่ตรง
 * คืนทั้ง tree ที่ตัดแล้ว และชุด id ที่ควรเปิดขยาย
 */
export function filterCategoryTree(
  tree: CategoryTreeNode[],
  query: string,
): { tree: CategoryTreeNode[]; expandIds: Set<string> } {
  const q = query.trim().toLowerCase();
  if (!q) return { tree, expandIds: new Set() };

  const expandIds = new Set<string>();

  const walk = (nodes: CategoryTreeNode[]): CategoryTreeNode[] => {
    const kept: CategoryTreeNode[] = [];
    for (const n of nodes) {
      const childKept = walk(n.children);
      const selfMatch = n.name.toLowerCase().includes(q);
      if (selfMatch || childKept.length > 0) {
        if (childKept.length > 0) expandIds.add(n.id);
        kept.push({ ...n, children: childKept });
      }
    }
    return kept;
  };

  return { tree: walk(tree), expandIds };
}

/** เก็บ id ทุกโหนดที่มีลูก (ใช้ตอนขยายทั้งหมด) */
export function collectExpandableIds(tree: CategoryTreeNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: CategoryTreeNode[]) => {
    for (const n of nodes) {
      if (n.children.length > 0) {
        ids.add(n.id);
        walk(n.children);
      }
    }
  };
  walk(tree);
  return ids;
}
